import { bopApi } from '@/lib/api/client';
import { withoutUndefined } from '@/lib/api/search-params';
import type { ReportType } from '@/services/api/reports';

const FEE_RUNS_PATH = 'v1/fee-runs';
const FEE_RUN_EXCLUSIONS_PATH = 'v1/fee-runs/exclusions';

export const FEE_RUN_REPORT_TYPE: ReportType = 'fee-run';

export type FeeRunStatus = 'CALCULATING' | 'CALCULATED' | 'CALCULATION_FAILED' | 'COLLECTING' | 'FINALISING' | 'COMPLETED' | 'PARTIAL' | 'CANCELLED';

/** Custody and platform fees are collected on different days, so a run is always one or the other. */
export type FeeScope = 'CUSTODY' | 'PLATFORM';

export type FeeType = 'custody' | 'platform';

export type TermsEntity = 'OTE' | 'OTM';

export type BalanceSource = 'fast' | 'instant-trade';

export type FeeRunUserStatus = 'PLANNED' | 'IN_PROGRESS' | 'DONE' | 'PARTIAL' | 'FAILED';

/**
 * UNKNOWN: the exchange's answer never arrived (transport error or gateway timeout), so the debit may or may not
 * have applied. It is never retried automatically; the next Retry reconciles it against the exchange's adjustments
 * log and settles it as CHARGED or FAILED.
 */
export type ChargeStatus = 'PENDING' | 'CHARGED' | 'SKIPPED' | 'FAILED' | 'UNKNOWN';

export type FundsStatus = 'NOT_APPLICABLE' | 'PENDING' | 'RECORDED' | 'FAILED';

export interface FeeSchedule {
  rate: number;
  minEur: number;
  maxEur: number | null;
  /** A charge worth less than this is not booked; dust sweeps are exempt. */
  minChargeEur: number;
}

/** Everything that decides what a run charges — frozen on the run at request time. */
export interface FeeRunParams {
  /** YYYY-MM-DD: the day the fees are taken, as the legacy job ran on a fixed day each month */
  chargeDate: string;
  feeScope: FeeScope;
  rates: Record<FeeType, FeeSchedule>;
  inactivityMonths: number;
  oteTermsCutoffDate: string;
  statusPrecedence: 'status-first' | 'terms-first';
  includeLockedBalances: boolean;
  includeFiat: boolean;
  sweepBelowMinimum: boolean;
  capScope: 'per-charge' | 'per-user';
  destinationAccounts: Record<BalanceSource, string>;
  guards: { maxUserFeeShareOfBalance: number; maxTotalFeeShareOfAum: number; maxCalculationAgeHours: number };
}

export type GuardName = 'PRICE_COMPLETENESS' | 'SNAPSHOT_COMPLETENESS' | 'PER_USER_CEILING' | 'RUN_CEILING' | 'STALENESS' | 'SINGLE_ACTIVE_RUN' | 'MANUAL_EXCLUSIONS';

export interface GuardResult {
  name: GuardName;
  passed: boolean;
  detail: string;
}

export interface FeeRunTotals {
  users: number;
  charges: number;
  totalEurBalance: string;
  originalFeeEur: string;
  finalFeeEur: string;
  byFeeType: Record<FeeType, { users: number; charges: number; feeEur: string }>;
  bySource: Record<BalanceSource, { charges: number; feeEur: string }>;
}

export interface FeeRunCounters {
  usersQueued: number;
  usersDone: number;
  usersPartial: number;
  usersFailed: number;
  chargesCharged: number;
  chargesSkipped: number;
  chargesFailed: number;
  chargesUnknown: number;
  fundsRecorded: number;
  fundsFailed: number;
}

export type FeeRunReports = Partial<Record<'planKey' | 'usersCsvKey' | 'chargesCsvKey' | 'exclusionsCsvKey' | 'treasuryCsvKey' | 'collectedCsvKey', string>>;

/** Fields every run carries from the moment it is requested. */
interface FeeRunCore {
  runId: string;
  /** YYYY-MM-DD */
  chargeDate: string;
  params: FeeRunParams;
  requestedBy: string;
  /** ISO-8601 */
  requestedAt: string;
  counters: FeeRunCounters;
  error?: string;
  updatedAt?: string;
}

/** Written once the calculation completes. */
interface FeeRunCalculation {
  calculatedAt: string;
  activityFrom: string;
  snapshotTimestamps: Partial<Record<BalanceSource, string | null>>;
  oteTermsVersions: string[];
  totals: FeeRunTotals;
  exclusionCounts: Record<string, number>;
  activityCounts: { exchangeAccounts: number; positionAccounts: number; instantTradePids: number; unresolvedAccounts: number };
  guards: GuardResult[];
  approvable: boolean;
  reports: FeeRunReports;
}

/** Written at approval. */
interface FeeRunApproval {
  approvedBy: string;
  approvedAt: string;
  approvalJustification: string;
  enqueue: { at: string; requested: number; sent: number; failed: number; failedPids?: string[] };
}

/** Written when collection finalises. */
interface FeeRunCompletion {
  completedAt: string;
  collected: { feeEur: string; users: number; partialUsers: number; byBucket: number };
}

interface FeeRunCancellation {
  cancelledBy: string;
  cancelledAt: string;
}

/** The keys exist on every member of the union (so `run.totals?.users` reads cleanly) but are typed absent where the status rules them out. */
type Absent<T> = { [K in keyof T]?: undefined };

export type CalculatingFeeRun = FeeRunCore & { status: 'CALCULATING' } & Absent<FeeRunCalculation & FeeRunApproval & FeeRunCompletion & FeeRunCancellation>;
export type CalculatedFeeRun = FeeRunCore & { status: 'CALCULATED' } & FeeRunCalculation & Absent<FeeRunApproval & FeeRunCompletion & FeeRunCancellation>;
export type CalculationFailedFeeRun = FeeRunCore & { status: 'CALCULATION_FAILED'; error: string } & Absent<
    FeeRunCalculation & FeeRunApproval & FeeRunCompletion & FeeRunCancellation
  >;
export type CollectingFeeRun = FeeRunCore & { status: 'COLLECTING' | 'FINALISING' } & FeeRunCalculation & FeeRunApproval & Absent<FeeRunCompletion & FeeRunCancellation>;
export type CompletedFeeRun = FeeRunCore & { status: 'COMPLETED' | 'PARTIAL' } & FeeRunCalculation & FeeRunApproval & FeeRunCompletion & Absent<FeeRunCancellation>;
/** A run can be discarded before or after its calculation landed, so the calculation is optional here. */
export type CancelledFeeRun = FeeRunCore & { status: 'CANCELLED' } & Partial<FeeRunCalculation> & FeeRunCancellation & Absent<FeeRunApproval & FeeRunCompletion>;

/** A fee run, discriminated on `status`: the fields a status cannot have are typed `undefined`, not merely optional. */
export type FeeRun = CalculatingFeeRun | CalculatedFeeRun | CalculationFailedFeeRun | CollectingFeeRun | CompletedFeeRun | CancelledFeeRun;

export interface FeeRunCharge {
  key: string;
  source: BalanceSource;
  uid: string;
  subaccountId: string;
  coin: string;
  balance: string;
  eurPrice: string;
  balanceEur: string;
  feeAmount: string;
  feeEur: string;
  /** What is left of the balance once the fee is taken; zero means the coin is emptied. */
  balanceAfter: string;
  capped: boolean;
  dust: boolean;
  transactionId: string;
  status: ChargeStatus;
  skipReason?: string;
  error?: string;
  /** When the debit was sent; set on every attempted charge, and what the UNKNOWN reconciliation searches from. */
  attemptedAt?: string;
  chargedAt?: string;
  fundsRecorded?: boolean;
}

export interface FeeRunUser {
  pid: string;
  mainFastId: string;
  userStatus: string;
  entity: TermsEntity;
  entityReason: string;
  feeType: FeeType;
  totalEurBalance: string;
  originalFeeEur: string;
  finalFeeEur: string;
  adjustedRate: string;
  adjustment: string;
  status: FeeRunUserStatus;
  charges: Record<string, FeeRunCharge>;
  fundsStatus: FundsStatus;
  chargedCount: number;
  skippedCount: number;
  failedCount: number;
  unknownCount: number;
  error?: string;
  attempts?: number;
  updatedAt?: string;
}

export interface FeeRunCapabilities {
  canView: boolean;
  canCalculate: boolean;
  canApprove: boolean;
}

export interface FeeRunsResponse {
  runs: FeeRun[];
  capabilities: FeeRunCapabilities;
  cursor?: string;
}

export interface FeeRunResponse {
  run: FeeRun;
  capabilities: FeeRunCapabilities;
}

export interface FeeRunUsersResponse {
  users: FeeRunUser[];
  cursor?: string;
}

export interface FeeRunsQuery {
  cursor?: string;
  limit?: number;
}

export interface FeeRunUsersQuery {
  status?: FeeRunUserStatus;
  cursor?: string;
  limit?: number;
}

/** The request body: charge date and fee type are required, everything else defaults server-side. */
export interface FeeRunRequest {
  chargeDate: string;
  feeScope: FeeScope;
}

export interface FeeRunApproveResponse {
  runId: string;
  status: FeeRunStatus;
  approvedAt: string;
  sent: number;
  failed: number;
}

/** 422 body when approval is blocked: the guards, with the failing ones marked. */
export interface FeeRunGuardsFailedBody {
  error: 'GUARDS_FAILED';
  guards: GuardResult[];
}

/** 422 body when the run was approved and is collecting, but some account holders could not be queued. Retry queues them. */
export interface FeeRunEnqueueIncompleteBody extends FeeRunApproveResponse {
  error: 'ENQUEUE_INCOMPLETE';
}

export const listFeeRuns = (query: FeeRunsQuery = {}) => bopApi.get(FEE_RUNS_PATH, { searchParams: withoutUndefined(query) }).json<FeeRunsResponse>();

export const getFeeRun = (runId: string) => bopApi.get(`${FEE_RUNS_PATH}/${encodeURIComponent(runId)}`).json<FeeRunResponse>();

export const getFeeRunUsers = (runId: string, query: FeeRunUsersQuery = {}) =>
  bopApi.get(`${FEE_RUNS_PATH}/${encodeURIComponent(runId)}/users`, { searchParams: withoutUndefined(query) }).json<FeeRunUsersResponse>();

/** Starts a calculation. The 202 carries the new run; poll it until it leaves CALCULATING. */
export const requestFeeRun = (body: FeeRunRequest) => bopApi.post(FEE_RUNS_PATH, { json: body }).json<FeeRun>();

/** Releases real debits — the API re-checks every guard and records the justification in the audit log. */
export const approveFeeRun = (runId: string, justification: string) =>
  bopApi.post(`${FEE_RUNS_PATH}/${encodeURIComponent(runId)}/approve`, { json: { justification } }).json<FeeRunApproveResponse>();

export const cancelFeeRun = (runId: string) => bopApi.post(`${FEE_RUNS_PATH}/${encodeURIComponent(runId)}/cancel`).json<{ runId: string; status: FeeRunStatus }>();

export const retryFeeRun = (runId: string) =>
  bopApi.post(`${FEE_RUNS_PATH}/${encodeURIComponent(runId)}/retry`).json<{ runId: string; status: FeeRunStatus; sent: number; failed: number }>();

/** An account holder ops have taken out of every fee run, whatever their balance or activity. */
export interface FeeRunExclusion {
  pid: string;
  /** One of the `reasons` the list returns */
  reason: string;
  comment: string;
  addedBy: string;
  /** ISO-8601 */
  addedAt: string;
}

export interface FeeRunExclusionReason {
  value: string;
  label: string;
}

export interface FeeRunExclusionsResponse {
  exclusions: FeeRunExclusion[];
  reasons: FeeRunExclusionReason[];
  capabilities: { canManage: boolean };
}

export interface SetFeeRunExclusionRequest {
  pid: string;
  reason: string;
  comment: string;
}

export const getFeeRunExclusions = () => bopApi.get(FEE_RUN_EXCLUSIONS_PATH).json<FeeRunExclusionsResponse>();

/** Adds the account holder, or replaces the reason and comment when they are already excluded. 404 USER_NOT_FOUND when the pid has no account. */
export const setFeeRunExclusion = ({ pid, ...body }: SetFeeRunExclusionRequest) =>
  bopApi.put(`${FEE_RUN_EXCLUSIONS_PATH}/${encodeURIComponent(pid)}`, { json: body }).json<{ exclusion: FeeRunExclusion }>();

export const removeFeeRunExclusion = (pid: string, comment: string) => bopApi.delete(`${FEE_RUN_EXCLUSIONS_PATH}/${encodeURIComponent(pid)}`, { json: { comment } }).text();
